import { getNormalizedGrossMarginPercentage } from '@/utils/percentage';
import { getNormalizedPriceInMicros } from '@/utils/money';
import { PricingAccountRow } from '@/experiences/sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { PricingEditField } from '@/experiences/sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';

export interface BaselineComparablePricingEdit {
  grossMarginPercentage: number;
  priceInMicros: number;
  updatedField: PricingEditField;
}

/**
 * Whether an edited (price, GM%) pair lands on the baseline it was edited
 * from, at displayed precision. Shared by the management edits and the agent
 * recommendation overrides so both agree on what counts as a no-op edit.
 */
export const isPricingAtBaseline = ({
  baselineGrossMarginPercentage,
  baselinePriceInMicros,
  grossMarginPercentage,
  priceInMicros,
  updatedField,
}: {
  baselineGrossMarginPercentage: number;
  baselinePriceInMicros: number | null;
  grossMarginPercentage: number;
  priceInMicros: number;
  updatedField: PricingEditField;
}): boolean => {
  const normalizedPrice = getNormalizedPriceInMicros(priceInMicros);
  const priceAxisMatch =
    normalizedPrice !== null &&
    normalizedPrice === getNormalizedPriceInMicros(baselinePriceInMicros);

  // For Price edits, only the price axis decides baseline. A small Price
  // change with an incidentally-stable rounded GM% is still a real
  // customer-facing change and must apply.
  if (updatedField === PricingEditField.Price) {
    return priceAxisMatch;
  }
  // For GM% edits, either axis matching at displayed precision is a no-op:
  // the cent-rounded price is what actually flows downstream, so a GM%
  // tweak that doesn't move the cent price is functionally inert.
  return (
    priceAxisMatch ||
    getNormalizedGrossMarginPercentage(grossMarginPercentage) ===
      getNormalizedGrossMarginPercentage(baselineGrossMarginPercentage)
  );
};

export const isEditAtOriginalBaseline = ({
  edit,
  originalRow,
}: {
  edit: BaselineComparablePricingEdit;
  originalRow: PricingAccountRow;
}): boolean =>
  isPricingAtBaseline({
    baselineGrossMarginPercentage: originalRow.grossMarginPercentage,
    baselinePriceInMicros: originalRow.priceInMicros,
    grossMarginPercentage: edit.grossMarginPercentage,
    priceInMicros: edit.priceInMicros,
    updatedField: edit.updatedField,
  });
